/**
 * Permission Management Component
 * 权限管理组件 - 详细展示和管理 ServiceAccount 的 RBAC 权限
 */

import {
  InfoCard,
  StatusOK,
  StatusWarning,
  StatusError,
  StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ClusterCheckResult, PermissionCheckResult } from './types';
import { RbacDataChainView } from './RbacDataChain';

const useStyles = makeStyles(theme => ({
  table: {
    minWidth: 650,
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  permissionDetail: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  categorySection: {
    marginBottom: theme.spacing(3),
  },
}));

interface PermissionManagementProps {
  result: ClusterCheckResult;
}

// 权限类别映射
const CATEGORY_LABELS: Record<string, string> = {
  k8sRead: 'K8s 只读权限',
  k8sManage: 'K8s 管理权限',
  tekton: 'Tekton 管理权限',
  knative: 'Knative 管理权限',
};

// 解析权限字符串为结构化数据
const parsePermission = (permStr: string): { resource: string; verbs: string[] } => {
  const match = permStr.match(/^(.+?):\s*(.+)$/);
  if (match) {
    return {
      resource: match[1],
      verbs: match[2].split(', '),
    };
  }
  return { resource: permStr, verbs: [] };
};

export const PermissionManagement: React.FC<PermissionManagementProps> = ({ result }) => {
  const classes = useStyles();

  if (result.connectionStatus === 'error') {
    return null;
  }

  // 构建权限对比表格数据
  const buildPermissionTable = (permission: PermissionCheckResult) => {
    const rows: Array<{
      resource: string;
      requiredVerbs: string[];
      actualVerbs: string[];
      status: 'ok' | 'partial' | 'missing';
    }> = [];

    // 解析已授权权限
    const allowedMap = new Map<string, string[]>();
    permission.allowed.forEach(perm => {
      const parsed = parsePermission(perm);
      allowedMap.set(parsed.resource, parsed.verbs);
    });

    // 解析缺失权限
    const deniedMap = new Map<string, string[]>();
    permission.denied.forEach(perm => {
      const parsed = parsePermission(perm);
      deniedMap.set(parsed.resource, parsed.verbs);
    });

    // 合并所有资源
    const allResources = new Set<string>();
    allowedMap.forEach((_, resource) => allResources.add(resource));
    deniedMap.forEach((_, resource) => allResources.add(resource));

    allResources.forEach(resource => {
      const allowedVerbs = allowedMap.get(resource) || [];
      const deniedVerbs = deniedMap.get(resource) || [];
      const requiredVerbs = [...allowedVerbs, ...deniedVerbs];

      let status: 'ok' | 'partial' | 'missing' = 'ok';
      if (deniedVerbs.length > 0 && allowedVerbs.length > 0) {
        status = 'partial';
      } else if (deniedVerbs.length > 0 && allowedVerbs.length === 0) {
        status = 'missing';
      }

      rows.push({
        resource,
        requiredVerbs,
        actualVerbs: allowedVerbs,
        status,
      });
    });

    return rows;
  };

  // SA 信息
  const metadata: Record<string, string> = {};
  if (result.serviceAccount) {
    metadata.ServiceAccount = `${result.serviceAccount.namespace}/${result.serviceAccount.name}`;
    if (result.serviceAccount.uid) {
      metadata['SA UID'] = result.serviceAccount.uid;
    }
  }
  metadata['集群名称'] = result.clusterTitle;
  metadata['集群 URL'] = result.clusterUrl;
  metadata['检测时间'] = new Date(result.checkedAt).toLocaleString('zh-CN');

  // 统计
  const totalPermissions = result.permissions.reduce((sum, p) => sum + p.allowed.length + p.denied.length, 0);
  const allowedCount = result.permissions.reduce((sum, p) => sum + p.allowed.length, 0);
  const deniedCount = result.permissions.reduce((sum, p) => sum + p.denied.length, 0);

  return (
    <>
    <InfoCard title="权限管理详情">
      <StructuredMetadataTable metadata={metadata} />
      
      <Box mt={2} mb={2}>
        <Typography variant="h6" gutterBottom>
          权限统计
        </Typography>
        <Box display="flex" style={{ gap: 16 }} mb={2}>
          <Chip label={`总计: ${totalPermissions}`} color="default" />
          <Chip label={`已授权: ${allowedCount}`} style={{ background: '#4caf50', color: 'white' }} />
          <Chip label={`缺失: ${deniedCount}`} style={{ background: '#f44336', color: 'white' }} />
        </Box>
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {result.permissions.map(permission => {
        const rows = buildPermissionTable(permission);
        const categoryLabel = CATEGORY_LABELS[permission.category] || permission.name;

        return (
          <Box key={permission.category} className={classes.categorySection}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={2}>
              {permission.status === 'ok' && <StatusOK />}
              {permission.status === 'partial' && <StatusWarning />}
              {permission.status === 'missing' && <StatusError />}
              <Typography variant="h6">{categoryLabel}</Typography>
              <Typography variant="caption" color="textSecondary">
                {permission.description}
              </Typography>
            </Box>

            {rows.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>资源</TableCell>
                      <TableCell>所需权限</TableCell>
                      <TableCell>实际权限</TableCell>
                      <TableCell>状态</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.permissionDetail}>
                          {row.resource}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" style={{ gap: 4 }} flexWrap="wrap">
                            {row.requiredVerbs.map(verb => (
                              <Chip
                                key={verb}
                                label={verb}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {row.actualVerbs.length > 0 ? (
                            <Box display="flex" style={{ gap: 4 }} flexWrap="wrap">
                              {row.actualVerbs.map(verb => (
                                <Chip
                                  key={verb}
                                  label={verb}
                                  size="small"
                                  style={{ background: '#4caf50', color: 'white' }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              无
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.status === 'ok' && <StatusOK>完整</StatusOK>}
                          {row.status === 'partial' && <StatusWarning>部分</StatusWarning>}
                          {row.status === 'missing' && <StatusError>缺失</StatusError>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="textSecondary">
                该类别未检测到权限信息
              </Typography>
            )}
          </Box>
        );
      })}

      {result.permissions.length === 0 && (
        <Box p={2} textAlign="center">
          <Typography color="textSecondary">
            未检测到权限信息，请检查集群连接和 ServiceAccount 配置
          </Typography>
        </Box>
      )}
    </InfoCard>

    {result.rbacDataChain && (
      <Box mt={3}>
        <RbacDataChainView dataChain={result.rbacDataChain} />
      </Box>
    )}
  </>
  );
};

